import axios from 'axios';
import { axiosClient } from '@/lib/axiosClient';
import { buildApiBase } from '@/lib/buildApiBase';

const PERM_API_BASE = `${buildApiBase()}/API/Permissions`;

// Tight retry policy for the permission fetch — 2 retries (3 total attempts),
// short backoff (250ms then 500ms), and ONLY on transient failures (network /
// 5xx). 401/403 are deliberate auth signals; retrying them just delays the
// real handling. Worst-case added latency on a permanent failure: ~750ms.
const PERM_FETCH_MAX_RETRIES = 2;
const PERM_FETCH_BACKOFF_MS = [250, 500];

function isTransientFetchError(err: unknown): boolean {
  if (!axios.isAxiosError(err)) {
    // Non-axios error (TypeError, etc.) — treat as transient. Rare but
    // shouldn't kill the session on the first occurrence.
    return true;
  }
  const status = err.response?.status;
  // No response at all = network / CORS / timeout — retry.
  if (status == null) return true;
  // Server-side transient — retry.
  if (status >= 500) return true;
  // 4xx (especially 401/403) = deliberate. Don't retry — surfaces the real
  // problem (expired session, missing AD group) without padded latency.
  return false;
}

/**
 * Mapping from G_PERMISSIONS key → backend function strings.
 *
 * Exact copy of the mapping in public/index.js checkPermissionsAndActivate().
 * If a user has ANY of the listed functions, the permission key is set to true
 * (or to the complex value after '=' if present).
 */
const PERMISSION_MAPPING: Record<string, string[]> = {
  auditViewPropertyAddresses: ['Audit:ViewPropertyAddresses'],
  auditReconvey: ['Audit:Reconvey'],
  auditReconvey2: ['Audit:ReconveyCore'],
  auditCoreScript: ['Audit:CoreScript'],
  damagesTab: [
    'System:ViewDamages',
    'System:UpdateMaintenanceOrder',
    'System:UpdateInspectionOrder',
  ],
  historyTab: [
    'System:ViewHistory',
    'System:ManageDamageInfoHistory',
    'System:ManageClaimInfoHistory',
    'System:CorrectDamageHistory',
  ],
  viewPostAuditHistory: ['System:ViewPostAuditHistory'],
  dashboardTab: ['System:ViewDashboard', 'SV_API_View'],

  bidsWFHistoryAll: ['Bids:WorkFlowHistoryAll'],
  bidsWFHistory: ['Bids:WorkFlowHistory'],
  oaTab: ['System:OAUser', 'System:OAManager', 'System:OAAdmin'],
  oaSubmitted: ['System:OAManager'],
  oaAdmin: ['System:OAAdmin'],
  oaModifyCosts: ['System:OAModifyCosts'],
  oaReports: ['System:OAReports'],
  oaPending: ['System:OAUser'],
  locationTab: ['System:GeoMap'],
  setGoldStandard: ['System:SetGoldStandard'],
  vendorView: ['System:VendorView'],
  propertyAdmin: ['System:PropertyAdmin'],
  systemAdmin: ['System:SpiGlassAdmin'],
  viewBanner: ['System:ViewBanner'],
  editDashboard: ['System:EditDashboard'],
  postAuditOrderCorrection: ['System:PostAuditOrderCorrection'],
  postAuditOrderCorrectionCore: ['System:PostAuditOrderCorrectionCore'],
  conveyImpedimentsCore: ['System:ConveyImpedimentsCore'],
  createOrderRequest: ['System:CreateOrderRequest'],
  createOrderRequestCore: ['System:CreateOrderRequestCore'],
  submitManualResultsCore: ['System:SubmitManualResultsCore'],
  createPropertyRequest: ['System:CreatePropertyRequest'],
  createPropertyRequestCore: ['System:CreatePropertyRequestCore'],
  viewWorkOrderDashboardTab: ['System:ViewWorkOrderDashboard'],
  editWorkOrderDashboard: ['System:EditWorkOrderDashboard'],
  clientAdmin: ['System:ClientAdmin'],
  clientAdminUpdater: ['ClientAdmin-Updater'],
  addText: ['System:AddText'],
  addTextCore: ['System:AddTextCore'],
  editText: ['System:EditText'],
  reassignOrder: ['System:ReassignOrder'],
  reassignOrder2: ['System:ReassignOrderCore'],
  sendMessage: ['System:SendMessage'],
  sendEmail: ['System:SendEmail'],
  invoiceVoid: ['Invoice:Void'],
  invoiceOnDemand: ['SV:InvoiceOnDemand'],
  invoiceOnDemand2: ['SV:InvoiceOnDemand2'],
  workOrderRebill: ['WorkOrder:Rebill'],
  workOrderComplete: ['WorkOrder:Complete'],
  invoiceReader: ['Invoice:Reader'],
  bypassAuditTasks: ['Audit:BypassAuditTasks'],
  bypassAuditDamages: ['Audit:BypassAuditDamages'],
  bypassAuditBilling: ['Audit:BypassAuditBilling'],
  bypassAuditBids: ['Audit:BypassAuditBids'],
  bypassAuditQuestions: ['Audit:BypassAuditQuestions'],
  generateDamagesPDF: ['System:GenerateDamagesPDF'],
  ICCStatus: ['Corrections:ICCStatus'],
  ICCConfirmation: ['Corrections:ICCConfirmation'],
  ICCStatusCore: ['Corrections:ICCStatusCore'],
  ICCConfirmationCore: ['Corrections:ICCConfirmationCore'],
  OrderModificationRequest: ['Corrections:WorkOrder'],
  OrderModificationRequestCore: ['Corrections:WorkOrderCore'],
  massUpload: ['System:MassUpload'],
  massUploadError: ['System:MassUploadError'],
  loanQualifier: ['Audit:LoanQualifier'],
  isSupport: ['Audit:ViewPropertyLoanID'],
  viewWorkOrderLuggage: ['Audit:ViewWorkOrderLuggage'],
  serviceRelease: ['Audit:ServiceRelease'],
  editApprovalExternalAutomationData: ['System:EditApprovalExternalAutomationData'],
  editTasksDuringVoid: ['System:EditDuringVoid'],
  addTaskGlobal: ['Audit:AddTaskGlobal'],
  postAuditPreInvoiceTaskChange: ['System:PstAudPreInvTskChnge'],
  catsubcatupdater: ['InvCatMapping:Updater'],
  catsubcatapprover: ['InvCatMapping:Approver'],
  catsubcatreader: ['InvCatMapping:Reader'],
  addTaskLog: ['System:TaskActLogNew'],
  viewTaskLog: ['System:TaskActLogRead'],
  propertyImageViewer: ['SV:PropertyDocs'],
  removeCancelDate: ['System:RemoveCancelDate'],
  pricingadminreader: ['PricingAdmin:Reader'],
  pricingadmincsreader: ['PricingAdmin:CSReader'],
  pricingadmincsupdater: ['PricingAdmin:CSUpdater'],
  pricingadmincsapprover: ['PricingAdmin:CSApprover'],
  pricingadminclientreader: ['PricingAdmin:ClientPricingReader'],
  pricingAdminRateSetupReader: ['PricingAdmin:RateSetupReader'],
  pricingAdminRateSetupUpdater: ['PricingAdmin:RateSetupUpdater'],
  pricingAdminRateSetupEnd: ['PricingAdmin:RateSetupEnd'],
  pricingAdminTaskMapReader: ['PricingAdmin:TaskMapReader'],
  pricingAdminTaskMapUpdater: ['PricingAdmin:TaskMapUpdater'],
  pricingAdminTaskMapEnd: ['PricingAdmin:TaskMapEnd'],
  pricingAdminLoanTypeMapReader: ['PricingAdmin:LoanTypeMapReader'],
  pricingAdminLoanTypeMapUpdater: ['PricingAdmin:LoanTypeMapUpdater'],
  pricingAdminLoanTypeMapEnd: ['PricingAdmin:LoanTypeMapEnd'],
  PricingAdminTestRendering: ['PricingAdmin:TestRendering'],
  pricingAdminDefaultRateReader: ['PricingAdmin:DefaultRateReader'],
  pricingAdminDefaultRateUpdater: ['PricingAdmin:DefaultRateUpdater'],
  pricingAdminDefaultRateEnd: ['PricingAdmin:DefaultRateEnd'],
  disputeThreshold: ['Disputes:Threshold'],
  disputeReader: ['Disputes:Reader'],
  disputeUpdater: ['Disputes:Updater'],
  pricingAdminRangeSetup: ['PricingAdmin:RangeSetupadmin'],
  clientRebill: ['Client:Rebill'],
  vendorRebill: ['Vendor:Rebill'],
  clientvendorRebill: ['ClientVendor:Rebill'],
  clientList: ['System:ClientList'],
  addClientmatrix: ['ClientMatrix:AddAttribute'],
  editClientmatrix: ['ClientMatrix:UpdateAttribute'],
  disputePaymentperiodsUpdater: ['Disputes:PaymentPeriodsUpdater'],
  prepaymentReader: ['Prepayments:Reader'],
  prepaymentUpdater: ['Prepayments:AddPrepayment'],
  vendorPenaltiesReader: ['VendorPenalties:Reader'],
  vendorPenaltiesUpdater: ['VendorPenalties:AddVendorPenalty'],
  CodeViolationRequest: ['System:EditCode'],
  CodeViolationRequestCore: ['System:EditCodeCore'],
  allowablesReader: ['Allowables:Reader'],
  allowablesUpdater: ['Allowables:Updater'],
  allowablesUploader: ['Allowables:Uploader'],
  allowablesCTD: ['Allowables:CTD'],
  allowablesCTDExport: ['Allowables:CTDExport'],
  rctManualSubmit: ['RCT:ManualSubmit'],
  unlockMobile: ['Vendor:UnlockMobile'],
  uploadCSV: ['Vendor:UploadCSVFile'],
  admin: ['Vendor:Admin'],
  companysearch: ['Vendor:CompanySearch'],
  serviceAreaReader: ['Vendor:Reader'],
  serviceAreaAdmin: [''],
  serviceAreaUpdater: ['Vendor:UpdateServiceAreaDetails'],
  contactsCreator: ['Vendor:AddContacts'],
  contactsUpdater: ['Vendor:UpdateContacts'],
  contactsMassUpdater: ['Vendor:MassUpdateContacts'],
  contactsDeleter: ['Vendor:DeleteContacts'],
  capUpdater: ['Vendor:EditCaps'],
  notesUpdater: ['Vendor:EditNotes'],
  linkServiceAreaUpdater: ['Vendor:EditLinkedServiceArea'],
  pricingCreator: ['Vendor:AddPricing'],
  pricingUpdater: ['Vendor:UpdatePricing'],
  pricingDeleter: ['Vendor:DeletePricing'],
  linkCompaniesUpdater: ['Vendor:EditLinkedCompanies'],
  uploadRankingCSV: ['Vendor:UploadRankingCSV'],
  rankInquiry: ['Vendor:RankInquiry'],
  rankMaintenance: ['Vendor:RankMaintenance'],
  updateAdminSystemVendor: ['Vendor:UpdateAdminSystemVendor'],
  editDiscount: ['Vendor:EditDiscount'],
  vendorTextReader: ['VendorText:Reader'],
  vendorTextUpdater: ['VendorText:Updater'],
  vendorTextUpdaterCore: ['VendorText:UpdaterCore'],
  editStatus: ['Vendor:EditStatus'],
  vendorCompanyNotes: ['Vendor:CompanyNotes'],
  editPayeeName: ['Vendor:EditPayeeName'],
  editCompanyDetails: ['Vendor:EditCompanyDetails'],
  viewCompanyDetails: ['Vendor:ViewCompanyDetails'],
  editWeeklyCap: ['Vendor:WeeklyCapacity'],
  viewDocuments: ['Vendor:ViewDocuments'],
  exPayment: ['Vendor:ExPayment'],
  allowLogin: ['Vendor:AllowLogin'],
  unlockUser: ['Vendor:UnlockUsername'],
  vmadmin: ['Vendor:UnlockUsername'],
  vmpermission: ['System:VendorLink'],
  vmachsSignUpComplete: ['Vendor:SignUpComplete'],
  vmachsEnrolled: ['Vendor:Enrolled'],
  vmpaymentsHold: ['Vendor:PaymentsOnHold'],
  vendorMemosUpdater: ['VendorMemos:Updater'],
  vendorTask: ['Vendor:VendorTasks'],
  vendorClientSchedule: ['Vendor:ClientSchedule'],
  vendorSchedule: ['Vendor:ScheduleOrder'],
  vendorScheduleCore: ['Vendor:ScheduleOrderCore'],
  adminLink: ['System:AdminLink'],
  categoriesReader: ['Categories:Reader'],
  auditReader: ['Audit:Reader'],
  categoriesUpdater: ['Categories:Updater'],
  attributeUpdater: ['Attributes:Updater'],
  attributeReader: ['Attributes:Reader'],
  componentsReader: ['Components:Reader'],
  componentsUpdater: ['Components:Updater'],
  keywordUpdater: ['Components:KeywordUpdater'],
  componentTest: ['ComponentTest'],
  autoTextReader: ['Components:AutoTextReader'],
  autoTextUpdater: ['Components:AutoText'],
  componentEndPermutation: ['Components:EndPermutation'],
  componentCSAprrover: ['Components:CSApprover'],
  componentCSUpdater: ['Components:CSUpdater'],
  SGStaff: ['SGGeneralUser:Access'],
  SafeSend: ['SV:SafeSend'],
  fileImportVendorCompany: ['System:FileImportVendorCompany'],
  searchEnabled: ['SV_API_View'],
  vmAddCompany: ['Vendor:AddCompany'],
  vmEditCompany: ['Vendor:EditCompany'],
  vmDeleteDocuments: ['Vendor:DeleteDocuments'],
  SACCButton: ['System:SACCButton'],
  SACCButtonCore: ['System:SACCButtonCore'],
  vmEditDocuments: ['Vendor:EditDocuments'],
  investorDecision: ['MS:InvestorDecision'],
  // Dispute Agent panel. String not yet confirmed against the permission
  // system — see features/disputes/disputeAgent/README.md.
  aiDisputeDecision: ['AI:DisputeDecision'],
  fitRepreviewButton: ['FIT:FitRepreviewButton'],
  promptLibrary: ['PromptLibrary:AccessAllowed'],
  communicationTool: ['Vendor:CommunicationTool'],
  videoInspection: ['System:360Video:Read'],
  canManageComplaintAssignment: ['ComplaintAssignment:ManageAllowed'],
  canAccessComplaintIntake: ['ComplaintIntake:AccessAllowed'],
  canAccessComplaintManagerOverview: ['ComplaintManagerOverview:AccessAllowed'],
  canManageComplaintRoutingRules: ['ComplaintRoutingRules:ManageAllowed'],
  tasklibraryReader: ['Tasklibrary:Reader'],
  tasklibraryAttributeReader: ['TasklibraryAttribute:Reader'],
  tasklibraryAttributeUpdater: ['TasklibraryAttribute:Updater'],
  tasklibraryPhotoRequirementReader: ['TasklibraryPhotoRequirement:Reader'],
  tasklibraryPhotoRequirementUpdater: ['TasklibraryPhotoRequirement:Updater'],
  tasklibrarySave: ['Tasklibrary:Save'],
  tasklibraryAddTask: ['Tasklibrary:AddTask'],
  addBid: ['Bids:AddBid'],
  bidAdd: ['System:Bid:Add'],
  taskAdd: ['System:Task:Add'],
  inviteMainVendor: ['lambda.vendorconnect.v1.VendorConnectPortal:InviteMainVendor'],
  createResource: ['Resources:Create'],
  editBGCStatus: ['Vendor:EditBGCStatus'],
};

// ============ RAW FUNCTION LIST CACHE ============
// The raw user function list is needed by validDynamic() which checks
// permissions that aren't in the PERMISSION_MAPPING (e.g., workflow
// transition permissions like "WF:Approve:PhaseIV").

let _cachedUserFunctionList: string[] | null = null;

/**
 * Fetch user function list from the permissions API and resolve it into
 * a G_PERMISSIONS-style object using the same mapping as index.js.
 *
 * Also caches the raw function list for validDynamic() usage.
 * Returns Record<string, unknown> matching the shape of $rootScope.G_PERMISSIONS.
 */
export async function fetchAndResolvePermissions(): Promise<Record<string, unknown>> {
  // Try the fetch up to PERM_FETCH_MAX_RETRIES + 1 times total. Re-throw the
  // last error if every attempt failed — caller (authStore) treats that as a
  // permissions-fetch failure and renders the error screen in prod / fails
  // open in dev (where obfuscate.ts then over-masks everything for safety).
  let lastError: unknown;
  for (let attempt = 0; attempt <= PERM_FETCH_MAX_RETRIES; attempt++) {
    try {
      const response = await axiosClient.get<string[]>(`${PERM_API_BASE}/GetPermissions/`, {
        // Session-level permission check — caller (`authStore` /
        // `LegacyPermissionsProvider`) handles 401 and 403 internally. Skip
        // the global toast/redirect.
        silent: true,
      } as never);
      const userFunctionList: string[] = response.data ?? [];
      _cachedUserFunctionList = userFunctionList;
      return resolvePermissions(userFunctionList);
    } catch (err) {
      lastError = err;
      // Don't burn retries on deliberate signals (401/403) or any 4xx — they
      // mean the server already gave us a definitive answer.
      if (!isTransientFetchError(err)) break;
      // Last attempt — no point sleeping before re-throwing.
      if (attempt === PERM_FETCH_MAX_RETRIES) break;
      await new Promise((resolve) => setTimeout(resolve, PERM_FETCH_BACKOFF_MS[attempt]));
    }
  }
  throw lastError;
}

/**
 * Check if a raw permission string exists in the user's function list.
 *
 * Exact port of permissionsService.validDynamic() from
 * public/SPIGlassApp/services/permissionsService.js lines 204-229.
 *
 * Unlike G_PERMISSIONS (which is a mapped/processed object), this checks
 * against the FULL and RAW permission list from the API. Used for dynamic
 * permissions like workflow transition permissions that cannot be predetermined.
 *
 * The React cache is the single source of truth — `fetchAndResolvePermissions()`
 * MUST be called (typically by `useAuthStore.initAuth`) before this returns
 * meaningful results. If the cache has never been populated, returns false.
 */
export function validDynamic(permissionString: string): boolean {
  if (!permissionString) return false;
  if (_cachedUserFunctionList == null) return false;

  for (const userFunction of _cachedUserFunctionList) {
    if (permissionString === userFunction) {
      return true;
    }
  }
  return false;
}

/** Test-only: reset the cached function list. */
export function __resetPermissionsCacheForTests(): void {
  _cachedUserFunctionList = null;
}

/**
 * Look up a raw permission string in the cached user function list.
 *
 * Port of `permissionsService.getPermission(permissionString)` from
 * public/SPIGlassApp/services/permissionsService.js (lines 164-198).
 *
 * Returns `{ validPermission: boolean, role?: string }`. `role` is the value
 * after `=` if the function entry has one (e.g. `Vendor:ViewEIN=R4` → role `'R4'`).
 *
 * If the cache hasn't been populated yet, returns `{ validPermission: false }`.
 */
export function getPermission(permissionString: string): {
  validPermission: boolean;
  role?: string;
} {
  if (!permissionString) return { validPermission: false };
  if (!_cachedUserFunctionList) return { validPermission: false };

  for (const userFunction of _cachedUserFunctionList) {
    const eqIndex = userFunction.indexOf('=');
    const left = eqIndex >= 0 ? userFunction.slice(0, eqIndex) : userFunction;
    const right = eqIndex >= 0 ? userFunction.slice(eqIndex + 1) : undefined;
    if (left === permissionString) {
      return { validPermission: true, role: right };
    }
  }
  return { validPermission: false };
}

/**
 * Complex spec form — matches the AngularJS contract.
 *
 * `preProcess`  runs over the (mutable) cached function list before resolution,
 *               letting callers filter/dedupe entries (e.g. keep only the
 *               highest-priority Expiration accessor).
 * `postProcess` transforms the resolved `complexValue` string before it lands
 *               in the permissions map (e.g. parse a duration string into a Date).
 */
export interface ComplexPermissionSpec {
  accessors: string[];
  preProcess?: (spec: ComplexPermissionSpec, userFunctionList: string[]) => void;
  postProcess?: (complexValue: string) => unknown;
}

export type PermissionSpecValue = string[] | ComplexPermissionSpec;

/**
 * Resolve a route-specific permission spec against the cached user function list
 * and merge the result into `authStore.permissions`.
 *
 * Port of `permissionsService.checkPermissions(spec)` from
 * public/SPIGlassApp/services/permissionsService.js. Used by routes that need
 * to authorize ad-hoc permissions not present in the global `PERMISSION_MAPPING`.
 *
 * Spec values can be:
 *   • `string[]` — simple accessor list. If any accessor matches, `permKey` is
 *     set to `true` (or to `=value` if the accessor is `Foo:Bar=R4`).
 *   • `ComplexPermissionSpec` — supports `preProcess`/`postProcess` hooks for
 *     specs that need to filter the function list or transform the resolved value.
 *
 * Returns the partial permission object for callers that want to inspect the
 * result; the merge into `authStore` is the side effect AngularJS callers
 * relied on (they re-read `G_PERMISSIONS` after awaiting).
 *
 * If the cache hasn't been populated yet (`fetchAndResolvePermissions` never ran),
 * resolves with an empty object — preserving the AngularJS behavior of failing
 * silently in that edge case.
 */
export async function checkPermissions(
  spec: Record<string, PermissionSpecValue>
): Promise<Record<string, unknown>> {
  if (!_cachedUserFunctionList || _cachedUserFunctionList.length === 0) {
    return {};
  }

  // Operate on a per-call copy so preProcess mutations don't bleed across calls.
  // (AngularJS shared the original list; copying is safer and matches the spirit
  // of the API since each call's preProcess only affects its own resolution.)
  const userFunctionList = [..._cachedUserFunctionList];

  const partial: Record<string, unknown> = {};

  for (const [permKey, value] of Object.entries(spec)) {
    const isComplex = !Array.isArray(value);
    const complexSpec = isComplex ? (value as ComplexPermissionSpec) : null;
    const accessors: string[] = isComplex ? complexSpec!.accessors : (value as string[]);

    if (complexSpec?.preProcess) {
      complexSpec.preProcess(complexSpec, userFunctionList);
    }

    for (const accessor of accessors) {
      for (const rawFunction of userFunctionList) {
        const eqIndex = rawFunction.indexOf('=');
        const userFunction = eqIndex >= 0 ? rawFunction.slice(0, eqIndex) : rawFunction;
        const complexValue = eqIndex >= 0 ? rawFunction.slice(eqIndex + 1) : undefined;

        if (userFunction === accessor) {
          if (complexValue == null) {
            partial[permKey] = true;
          } else if (complexSpec?.postProcess) {
            partial[permKey] = complexSpec.postProcess(complexValue);
          } else {
            partial[permKey] = complexValue;
          }
        } else if (
          userFunction.includes('|') &&
          userFunction.toUpperCase().includes(accessor.toUpperCase())
        ) {
          const v = userFunction.toUpperCase().split(permKey.toUpperCase())[1];
          const listKey = `${permKey}List`;
          const byPassList = (partial[listKey] as string[]) ?? [];
          byPassList.push(permKey + v);
          partial[listKey] = byPassList;
        }
      }
    }
  }

  // Lazy-import to avoid a circular dep with authStore (authStore imports
  // fetchAndResolvePermissions from this file).
  const { useAuthStore } = await import('@/store/authStore');
  useAuthStore.getState().mergePermissions(partial);

  return partial;
}

/**
 * Get the cached raw user function list, or null if not yet fetched.
 */
export function getRawUserFunctionList(): string[] | null {
  return _cachedUserFunctionList;
}

/**
 * Resolve a raw user function list into the G_PERMISSIONS object.
 * Exact port of permissionsService.checkPermissions() from
 * public/SPIGlassApp/services/permissionsService.js.
 */
export function resolvePermissions(userFunctionList: string[]): Record<string, unknown> {
  const permissions: Record<string, unknown> = {};

  for (const [permKey, accessors] of Object.entries(PERMISSION_MAPPING)) {
    for (const accessor of accessors) {
      for (const rawFunction of userFunctionList) {
        const eqIndex = rawFunction.indexOf('=');
        const userFunction = eqIndex >= 0 ? rawFunction.slice(0, eqIndex) : rawFunction;
        const complexValue = eqIndex >= 0 ? rawFunction.slice(eqIndex + 1) : undefined;

        if (userFunction === accessor) {
          permissions[permKey] = complexValue ?? true;
        } else if (
          userFunction.includes('|') &&
          userFunction.toUpperCase().includes(accessor.toUpperCase())
        ) {
          const value = userFunction.toUpperCase().split(permKey.toUpperCase())[1];
          const listKey = `${permKey}List`;
          const byPassList = (permissions[listKey] as string[]) ?? [];
          byPassList.push(permKey + value);
          permissions[listKey] = byPassList;
        }
      }
    }
  }

  return permissions;
}

// Text obfuscation helpers (`obfuscateText`, `obfuscateTextByPermission`)
// previously lived here as a parallel implementation that took the
// PERMISSION_MAPPING key as input. They have been removed in favor of the
// single canonical implementation in `@/lib/obfuscate` which takes the raw
// permission string (e.g. `'Audit:ViewLoanNumber'`) — matching the legacy
// AngularJS `permissionsService.obfuscateTextByPermission` signature that
// every existing call site already used.

// ============================================================================
// WORK ORDER PERMISSION (port of permissionsService.workOrderPermission)
// ============================================================================

export const CONST_WORKORDER_RULE = {
  DoNotAllowPrePhaseCheck: 'DoNotAllowPrePhaseCheck',
  DoNotAllowBeforePhaseIV: 'DoNotAllowBeforePhaseIV',
} as const;

type WorkOrderRule = (typeof CONST_WORKORDER_RULE)[keyof typeof CONST_WORKORDER_RULE];

interface WorkflowStateTransition {
  currentState?: {
    code?: string;
  };
}

const PRE_PHASE_CHECK_BLOCKED_STATES = new Set([
  'ResultsInHouse',
  'OrderReceivedFailed',
  'RetryOrderReceived',
  'DamagesSaved',
  'DamagesSaveFailed',
  'RetryDamagesBidsSave',
  'TasksSaveFailed',
  'TasksSaved',
  'RetryTaskSave',
  'PricingSaved',
  'PricingSaveFailed',
  'RetryPricingSave',
  'SubmissionSaved',
  'SubmissionSaveFailed',
  'RetrySubmissionSave',
  'PhaseCheck',
]);

/**
 * Check if a work order action is permitted based on workflow state.
 * Port of permissionsService.workOrderPermission() from AngularJS.
 */
export function workOrderPermission(
  wfTrans: WorkflowStateTransition | null | undefined,
  rule: WorkOrderRule
): boolean {
  if (rule === CONST_WORKORDER_RULE.DoNotAllowPrePhaseCheck) {
    if (!wfTrans) return false;
    if (PRE_PHASE_CHECK_BLOCKED_STATES.has(wfTrans.currentState?.code ?? '')) return false;
  }

  if (rule === CONST_WORKORDER_RULE.DoNotAllowBeforePhaseIV) {
    if (!wfTrans) return false;
    if (wfTrans.currentState?.code === 'PreSPIGlassAudit') return false;
  }

  return true;
}
